/* eslint-disable prettier/prettier */
/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Avatar, ListItemButton, ListItemIcon, Tooltip, useMediaQuery } from '@mui/material';
import Chip from '@mui/material/Chip';
import { MENU_OPEN, SET_MENU } from 'store/actions';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const isOpen = customization.isOpen.includes(item.id);

    // Tooltip & hover states
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipBlocked, setTooltipBlocked] = useState(false);

    // Local timeouts (not global)
    const hoverTimeoutRef = useRef(null);
    const unblockTimeoutRef = useRef(null);

    // Icons
    const SIcon = item.selected_icon;
    const SelectedIcon = SIcon ? (
        <SIcon strokeWidth={1.5} size="1.3rem" style={{ width: '22px', height: '22px' }} />
    ) : (
        <FiberManualRecordIcon sx={{ width: 8, height: 8 }} />
    );

    const Icon = item?.icon;
    const itemIcon = Icon ? (
        <Icon stroke={1.5} size="1.3rem" style={{ width: '22px', height: '22px' }} />
    ) : (
        <FiberManualRecordIcon sx={{ width: isOpen ? 8 : 6, height: isOpen ? 8 : 6 }} fontSize={level > 0 ? 'inherit' : 'medium'} />
    );

    // Target + Routing
    const itemTarget = item.target ? '_blank' : '_self';
    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
    };
    if (item.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    // Menu click
    const handleMenu = () => {
        dispatch({ type: MENU_OPEN, id: item.id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    // Active state on page load
    useEffect(() => {
        const match = document.location.pathname.split('/').includes(item.id);
        if (match) dispatch({ type: MENU_OPEN, id: item.id });

        // Cleanup
        return () => {
            clearTimeout(hoverTimeoutRef.current);
            clearTimeout(unblockTimeoutRef.current);
        };
    }, [pathname, item.id, dispatch]);

    // Hover handling — isolated per item
    const handleMouseEnter = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (!tooltipBlocked) {
            hoverTimeoutRef.current = setTimeout(() => {
                setTooltipVisible(true);
            }, 50); // small delay avoids flicker
        }
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setTooltipVisible(false);
    };

    // Click handling — block tooltip briefly
    const handleClick = () => {
        setTooltipVisible(false);
        setTooltipBlocked(true);

        clearTimeout(hoverTimeoutRef.current);
        clearTimeout(unblockTimeoutRef.current);

        unblockTimeoutRef.current = setTimeout(() => {
            setTooltipBlocked(false);
        }, 50);

        handleMenu();
    };

    // Button element
    const listItemButton = (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDragStart={(e) => e.preventDefault()}
            selected={isOpen}
            sx={{
                height: '40px !important',
                width: '40px !important',
                borderRadius: '5px',//`${customization.borderRadius}px`,
                mb: '10px',
                // alignItems: 'flex-start',
                alignItems:'center',
                backgroundColor: tooltipVisible || isOpen ? '#D9EAFF !important' : 'inherit',
                transition: 'background-color 0.2s ease-in-out',
                padding: '5px',
                justifyContent:'center'
                // py: level > 1 ? 1 : 1.25,
                // pl: `${level * 24}px`
            }}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 22 }}>
                {isOpen || tooltipVisible ? SelectedIcon : itemIcon}
            </ListItemIcon>

            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );

    // Tooltip only when hover is active
    return tooltipVisible && !tooltipBlocked ? (
        <Tooltip
            title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{item.title}</span>
                </span>
            }
            arrow
            placement="right"
            disableFocusListener
            disableTouchListener
            disableInteractive
            PopperProps={{
                modifiers: [
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', options: { boundary: 'clippingParents' } },
                    { name: 'flip', enabled: false }
                ]
            }}
            componentsProps={{
                tooltip: {
                    sx: {
                        fontFamily: isOpen ? 'Open Sans Bold' : 'Open Sans Regular',
                        backgroundColor: '#D9EAFF',
                        color: '#03045e',
                        fontSize: '14px',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                    }
                },
                arrow: {
                    sx: { color: '#D9EAFF' }
                }
            }}
        >
            {listItemButton}
        </Tooltip>
    ) : (
        listItemButton
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
